// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the firefly SCHEDULE: a pure function of (mode, seed) and THE ENTIRE P2P-sync
// surface. Every peer builds the identical list of motes, so a same-seed race needs no
// game state on the wire. No Math.random — a seeded mulberry per zone.
//
// KEY INVARIANT (asserted in tests): at most ONE mote is alive in a zone at any instant.
// Each zone runs an independent timeline — spawn a mote for `lifeMs`, then a gap, then
// the next — so a swat on a zone is unambiguous (there is only ever one thing to catch
// there). A mote is only included if its whole life fits inside the round, so the set of
// fireflies is a fixed, knowable "perfect" the results screen can score everyone against.

import { makeRng, randFloat } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';
import { zonesOf } from './modes';

export type MoteKind = 'fly' | 'wasp';

export interface Mote {
  /** Stable id = index in the tSpawn-sorted schedule. */
  id: number;
  zone: number;
  kind: MoteKind;
  tSpawn: number;
  tExpire: number;
}

/** Build the full, deterministic list of motes for a board, sorted by spawn time. */
export function buildSchedule(mode: Mode, seed: number): Mote[] {
  const zones = zonesOf(mode);
  const out: Array<Omit<Mote, 'id'>> = [];

  for (let z = 0; z < zones; z++) {
    const rng = makeRng(`waft:${mode.id}:${seed >>> 0}:${z}`);
    let t = randFloat(rng, 0, mode.leadMs);
    // Guard against a pathological zero-progress loop; the real bound is limitMs.
    let guard = 0;
    while (t + mode.lifeMs <= mode.limitMs && guard < 5000) {
      guard++;
      const kind: MoteKind = rng() < mode.waspRate ? 'wasp' : 'fly';
      const tSpawn = t;
      const tExpire = t + mode.lifeMs;
      out.push({ zone: z, kind, tSpawn, tExpire });
      const gap = randFloat(rng, mode.gapMinMs, mode.gapMaxMs);
      t = tExpire + gap;
    }
  }

  out.sort((a, b) => a.tSpawn - b.tSpawn || a.zone - b.zone);
  return out.map((m, id) => ({ ...m, id }));
}

/** How many fireflies (not wasps) are in this schedule — the catchable "perfect". */
export function flyCount(schedule: readonly Mote[]): number {
  let n = 0;
  for (const m of schedule) if (m.kind === 'fly') n++;
  return n;
}
